import type { ReactNode } from 'react';

export interface HeaderStat {
  label: string;
  value: ReactNode;
  tone?: 'good' | 'bad' | 'warn' | 'muted';
}

/**
 * Page header: a title, an optional action cluster, and a row of live figures.
 *
 * Replaces the design system's PageHeader here because eyebrow + title +
 * subtitle stacked three lines of text and none of them said anything useful —
 * "Conscious Spending Plan" was on every page. A summary row of real numbers
 * earns the space instead.
 *
 * Below the tablet tier the `<h1>` is visually hidden rather than dropped. The
 * shell's sticky top bar already carries the route title on a phone, and the
 * bottom nav highlights it a third time — three copies of the word
 * "Projections" stacked down a 700px viewport, the top two of them pinned. The
 * heading stays in the accessibility tree because the top bar's copy is a plain
 * `<div>`, so removing this one would leave the page with no `h1` at all.
 */
export function RouteHeader({
  title, actions, stats, children,
}: {
  title: string;
  actions?: ReactNode;
  stats?: HeaderStat[];
  children?: ReactNode;
}) {
  return (
    <header className="vd-routehead">
      <div className="vd-routehead__row">
        <h1 className="vd-routehead__title">{title}</h1>
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>

      {stats && stats.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
          gap: '6px 18px', marginTop: 10,
        }}>
          {stats.map((s, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
              <span style={{
                fontFamily: 'var(--vn-font-mono)', fontSize: 9.5, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--vn-text-muted)',
              }}>
                {s.label}
              </span>
              <span className="vd-num" style={{ fontSize: 13, fontWeight: 600, color: toneColour(s.tone) }}>
                {s.value}
              </span>
            </span>
          ))}
        </div>
      )}

      {children}
    </header>
  );
}

function toneColour(t?: HeaderStat['tone']) {
  switch (t) {
    case 'good': return 'var(--vn-success)';
    case 'bad': return 'var(--vn-danger)';
    case 'warn': return 'var(--vn-warning)';
    case 'muted': return 'var(--vn-text-muted)';
    default: return 'var(--vn-text-primary)';
  }
}

/**
 * Sub-navigation for routes with more than one view (Projections / Scenarios).
 *
 * `role="group"` with `aria-pressed`, not `tablist`/`tab` — the same call
 * `SegTabs` makes, and for the same reason. These buttons switch *routes*: each
 * one navigates, there is no `tabpanel` for `aria-controls` to point at, and
 * nothing here implements the arrow-key roving focus the tabs pattern promises.
 * Announcing "tab 1 of 2" and then not responding to Left/Right is worse for a
 * screen-reader user than never claiming the pattern, because the failure is
 * silent — the affordance was advertised and simply doesn't work.
 */
export function SubNav({ items, active, onSelect, label = 'Section' }: {
  items: { id: string; label: string; icon?: React.ComponentType<{ size?: number }> }[];
  active: string;
  onSelect: (id: string) => void;
  label?: string;
}) {
  return (
    <div className="vd-subnav" role="group" aria-label={label}>
      {items.map(item => {
        const on = active === item.id;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(item.id)}
            className={`vd-subnav__tab${on ? ' is-active' : ''}`}
          >
            {Icon && <Icon size={14} aria-hidden />}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
